import * as z from 'zod';

import { zodDateOrString } from '../../utils';

/**
 * #### Fannie Mae LAR 32 Report: Servicing Transfer Record
 *
 * The LAR 32 report is a 55-character fixed length string. Character lengths and positions are noted at the individual fields.
 *
 * From Fannie's docs:
 *
 * A servicer that has received approval for transfer of servicing must transmit loan-level information for the
 * mortgage loans that will be included in the transfer to the Fannie Mae investor reporting system. The following
 * table provides additional instructions regarding these transactions.
 *
 * #### The servicer must…
 * - Complete a Request for Approval of Servicing or Subservicing Transfer (Form 629)34T and email it to the Regional
 *   Office at least 60 days prior to the Effective Date.
 *   - Note: Form 629 is only required for external transfers where the first five digits are different and it does
 *     not impact a sub-servicer transfer.
 * - Finalize the transfer request by the 25th calendar day of the month prior to the transfer effective date.
 * - Use Transaction Type 32 (Servicing Transfer Record) to provide this information.
 * - Submit this loan-level information in time for Fannie Mae to receive it no later than 15 days before the effective
 *   date of the transfer; although it may be submitted as early as six months before the proposed effective date.
 */
export const FannieMaeLar32V1 = z.object({
  version: z.literal('1').describe('version'),
  /**
   * ##### Transferor Lender Number
   * The lender who is getting rid of the loan. Numeric length 9; characters 1 - 9. This field is followed by a blank space in the report itself.
   */
  transferorLenderNumber: z.string().describe('string'),
  /**
   * ##### Record Identifier
   * Always `'32'`; characters 11 - 12
   */
  recordIdentifier: z.literal('32').describe('literal'),
  /**
   * ##### Source Code
   * Always `'0'`; character 13
   */
  sourceCode: z.literal('0').describe('literal'),
  /**
   * ##### Fannie Mae Loan Number
   * Numeric length 10; characters 14 - 23
   */
  fannieMaeLoanNumber: z.string().describe('string'),
  /**
   * ##### Request Effective Date
   * Numeric length 6, YYYYMM format; characters 24 - 29. E.g. for a transfer that is effective January 31, 2003, use 200301.
   */
  requestEffectiveDate: zodDateOrString.describe('date'),
  /**
   * ##### Transferee Lender Number
   * The lender who is receiving the loan. Numeric length 9; characters 30 - 38.
   */
  transfereeLenderNumber: z.string().describe('string'),
  /**
   * ##### Lender Loan ID
   * Alphanumeric length 15; characters 39 - 53
   */
  lenderLoanId: z.string().describe('string'),
  /**
   * ##### Transfer Request Type Code
   * One of:
   * - `00`: Non-MBS Loan
   * - `10`: MBS Loan
   * Numeric length 2; characters 54 - 55
   */
  transferRequestTypeCode: z.enum(['00', '01']).describe('enum'),
});
export type FannieMaeLar32V1 = z.infer<typeof FannieMaeLar32V1>;

export const FannieMaeLar32 = FannieMaeLar32V1;
export type FannieMaeLar32 = z.infer<typeof FannieMaeLar32>;
// Make `FannieMaeLar32` a discriminated union on 'version' when changes happen
// export const FannieMaeLar32V2 = z.object({
//   version: z.literal('2'),
//   foo: z.string(),
// });
// export const FannieMaeLar32 = z.discriminatedUnion('version', [FannieMaeLar32V1, FannieMaeLar32V02012025]);
